/**
 * A screen outside the shell: one centred card, with the language and theme
 * controls in the corner.
 *
 * For the screens somebody stands on before they can use the application —
 * signing in, and the password change an administrator required. The controls
 * are here, not behind sign-in, for the reason `LanguageSelector` is on the
 * sign-in screen: somebody who cannot read the form cannot get past it to find
 * a language setting.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { LanguageSelector } from './LanguageSelector';
import { useColorMode } from '../theme/ColorModeProvider';

export interface StandaloneScreenProps {
  children: ReactNode;
}

export function StandaloneScreen({ children }: StandaloneScreenProps) {
  const { t } = useTranslation('common');
  const { mode, toggle } = useColorMode();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        // No background here: the photograph is painted behind every screen
        // by the theme's CssBaseline override.
      }}
    >
      <Stack direction="row" spacing={0.5} sx={{ position: 'fixed', top: 12, right: 12 }}>
        <LanguageSelector />
        <Tooltip title={mode === 'dark' ? t('theme.toLight') : t('theme.toDark')}>
          <IconButton onClick={toggle} aria-label={t('theme.toggle')}>
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      <Paper
        variant="outlined"
        sx={{ width: '100%', maxWidth: 420, p: { xs: 3, sm: 4 }, borderRadius: 3 }}
      >
        {children}
      </Paper>
    </Box>
  );
}
